import { withErrorCode } from '@/lib/errors'
import { formatUserFacingError } from '@/lib/errors'
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { storage, ref as storageRef, uploadBytes, uploadBytesResumable, getDownloadURL } from '@/lib/firebase/storage'
import { serverTimestamp } from 'firebase/firestore'
import { AdminFormPage } from '../components/AdminFormPage'
import { MemberSearchPicker } from '@/components/common/MemberSearchPicker'
import { adminFilmsService } from '../services/admin.films.service'
import { adminSeasonsService } from '../services/admin.seasons.service'
import { adminCompanyService } from '../services/admin.company.service'
import { typedDoc, updateDocument } from '@/lib/firebase/firestore'
import type { SeasonDocument, CompanyMemberDocument, FilmDocument } from '@/types'

const memberRefSchema = z.object({
  name: z.string().min(1),
  slug: z.string().min(1),
  photoUrl: z.string(),
})

const filmSchema = z.object({
  type: z.enum(['episode', 'project']),
  title: z.string().min(1, 'Title is required'),
  seasonId: z.string().optional(),
  seasonNumber: z.number().int().optional(),
  episodeNumber: z.number().int().optional(),
  description: z.string().min(1, 'Description is required'),
  youtubeId: z.string().optional(),
  isPublished: z.boolean(),
  order: z.number().int().min(0),
  choreographers: z.array(memberRefSchema),
  cast: z.array(memberRefSchema),
  crew: z.array(memberRefSchema),
}).superRefine((data, ctx) => {
  if (data.type === 'episode') {
    if (!data.seasonId) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Season is required', path: ['seasonId'] })
    }
    if (!data.episodeNumber || data.episodeNumber < 1) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Episode number must be at least 1', path: ['episodeNumber'] })
    }
  }
})

type FilmFormValues = z.infer<typeof filmSchema>

export function AdminFilmEditPage() {
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const isNew = !id || id === 'new'
  const navigate = useNavigate()

  const initialType = searchParams.get('type') === 'project' ? 'project' : 'episode'

  const [loading, setLoading] = useState(!isNew)
  const [seasons, setSeasons] = useState<Array<SeasonDocument & { id: string }>>([])
  const [members, setMembers] = useState<Array<CompanyMemberDocument & { id: string }>>([])
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [thumbnailUrl, setThumbnailUrl] = useState<string>('')
  const [thumbnailUploading, setThumbnailUploading] = useState(false)
  const thumbnailFileInputRef = useRef<HTMLInputElement>(null)
  const [videoUrl, setVideoUrl] = useState<string>('')
  const [videoUploading, setVideoUploading] = useState(false)
  const [videoUploadProgress, setVideoUploadProgress] = useState<number>(0)
  const videoFileInputRef = useRef<HTMLInputElement>(null)

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FilmFormValues>({
    resolver: zodResolver(filmSchema),
    defaultValues: {
      type: initialType,
      title: '',
      seasonId: '',
      seasonNumber: 1,
      episodeNumber: 1,
      description: '',
      youtubeId: '',
      isPublished: false,
      order: 0,
      choreographers: [],
      cast: [],
      crew: [],
    },
  })

  const watchedType = watch('type')
  const watchedSeasonId = watch('seasonId')
  const watchedTitle = watch('title')
  const choreographers = watch('choreographers')
  const cast = watch('cast')
  const crew = watch('crew')

  const isEpisode = watchedType === 'episode'
  const backTo = isEpisode ? '/admin/config/films' : '/admin/config/projects'

  // Keep seasonNumber in sync with the selected season
  useEffect(() => {
    if (!watchedSeasonId) return
    const found = seasons.find((s) => s.id === watchedSeasonId)
    if (found) {
      setValue('seasonNumber', found.number)
    }
  }, [watchedSeasonId, seasons, setValue])

  // Load seasons and company members
  useEffect(() => {
    adminSeasonsService
      .getAllSeasons()
      .then(setSeasons)
      .catch((err: unknown) =>
        setSubmitError(formatUserFacingError(err, 'Failed to load seasons'))
      )

    adminCompanyService
      .getAllMembers()
      .then(setMembers)
      .catch((err: unknown) =>
        setSubmitError(formatUserFacingError(err, 'Failed to load members'))
      )
  }, [])

  // Load existing film when editing
  useEffect(() => {
    if (isNew || !id) return
    setLoading(true)
    adminFilmsService
      .getFilmById(id)
      .then((found) => {
        if (found) {
          const filmType = found.type ?? 'episode'
          reset({
            type: filmType,
            title: found.title,
            seasonId: found.seasonId ?? '',
            seasonNumber: found.seasonNumber ?? 1,
            episodeNumber: found.episodeNumber ?? 1,
            description: found.description,
            youtubeId: found.youtubeId,
            isPublished: found.isPublished,
            order: found.order,
            choreographers: found.choreographers,
            cast: found.cast ?? [],
            crew: found.crew ?? [],
          })
          if (found.thumbnailUrl) setThumbnailUrl(found.thumbnailUrl)
          if (found.videoUrl) setVideoUrl(found.videoUrl)
        }
      })
      .catch((err: unknown) =>
        setSubmitError(formatUserFacingError(err, 'Failed to load film'))
      )
      .finally(() => setLoading(false))
  }, [id, isNew, reset])

  async function handleThumbnailUpload(file: File) {
    const slug = watchedTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'film'
    setThumbnailUploading(true)
    try {
      const fileRef = storageRef(storage, `films/${slug}/thumbnail`)
      await uploadBytes(fileRef, file)
      const url = await getDownloadURL(fileRef)
      setThumbnailUrl(url)

      if (!isNew && id) {
        const docRef = typedDoc<FilmDocument>('films', id)
        await updateDocument(docRef, {
          thumbnailUrl: url,
          updatedAt: serverTimestamp(),
        })
      }
    } catch (err) {
      setSubmitError(formatUserFacingError(err, 'Thumbnail upload failed'))
    } finally {
      setThumbnailUploading(false)
    }
  }

  async function handleVideoUpload(file: File) {
    const slug = watchedTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'film'
    setVideoUploading(true)
    setVideoUploadProgress(0)
    try {
      const fileRef = storageRef(storage, `films/${slug}/video`)
      const task = uploadBytesResumable(fileRef, file)
      await new Promise<void>((resolve, reject) => {
        task.on(
          'state_changed',
          (snapshot) => {
            setVideoUploadProgress(Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100))
          },
          reject,
          resolve,
        )
      })
      const url = await getDownloadURL(fileRef)
      setVideoUrl(url)

      if (!isNew && id) {
        const docRef = typedDoc<FilmDocument>('films', id)
        await updateDocument(docRef, {
          videoUrl: url,
          updatedAt: serverTimestamp(),
        })
      }
    } catch (err) {
      setSubmitError(formatUserFacingError(err, 'Video upload failed'))
    } finally {
      setVideoUploading(false)
    }
  }

  async function onSubmit(values: FilmFormValues) {
    setSubmitError(null)
    try {
      const thumbnail = thumbnailUrl || undefined
      const video = videoUrl || undefined
      if (isNew) {
        await adminFilmsService.createFilm(values, thumbnail, video)
      } else if (id) {
        await adminFilmsService.updateFilm(id, values, thumbnail, video)
      }
      navigate(backTo)
    } catch (err) {
      setSubmitError(formatUserFacingError(err, 'Failed to save film'))
    }
  }

  return (
    <AdminFormPage
      title={isNew ? 'New Film' : 'Edit Film'}
      cardTitle={isNew ? 'Create a new film' : 'Update film details'}
      backTo={backTo}
      loading={loading}
      cardSkeletonClassName="h-96"
    >
      <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} className="space-y-5">
        {/* Film Type toggle */}
        <div className="space-y-1">
          <Label>Film Type</Label>
          <div className="flex rounded-lg border overflow-hidden w-fit">
            <button
              type="button"
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                isEpisode
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-background text-muted-foreground hover:bg-muted'
              }`}
              onClick={() => setValue('type', 'episode')}
            >
              Season Episode
            </button>
            <button
              type="button"
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                !isEpisode
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-background text-muted-foreground hover:bg-muted'
              }`}
              onClick={() => setValue('type', 'project')}
            >
              Project
            </button>
          </div>
        </div>

        <div className="space-y-1">
          <Label htmlFor="title">Title</Label>
          <Input id="title" type="text" placeholder="Film title" {...register('title')} />
          {errors.title && (
            <p className="text-sm text-destructive">{withErrorCode(String(errors.title.message), 'app/validation')}</p>
          )}
        </div>

        {/* Season + episode fields — episode type only */}
        {isEpisode && (
          <>
            <div className="space-y-1">
              <Label htmlFor="seasonId">Season</Label>
              <Select
                value={watchedSeasonId}
                onValueChange={(val) => setValue('seasonId', val)}
              >
                <SelectTrigger id="seasonId" className="w-full">
                  <SelectValue placeholder="Select a season" />
                </SelectTrigger>
                <SelectContent>
                  {seasons.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.seasonId && (
                <p className="text-sm text-destructive">{withErrorCode(String(errors.seasonId.message), 'app/validation')}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="episodeNumber">Episode Number</Label>
                <Input
                  id="episodeNumber"
                  type="number"
                  min={1}
                  {...register('episodeNumber', { valueAsNumber: true })}
                />
                {errors.episodeNumber && (
                  <p className="text-sm text-destructive">{withErrorCode(String(errors.episodeNumber.message), 'app/validation')}</p>
                )}
              </div>

              <div className="space-y-1">
                <Label htmlFor="order">Sort Order</Label>
                <Input id="order" type="number" min={0} {...register('order', { valueAsNumber: true })} />
                {errors.order && (
                  <p className="text-sm text-destructive">{withErrorCode(String(errors.order.message), 'app/validation')}</p>
                )}
              </div>
            </div>
          </>
        )}

        {/* Sort order for projects (standalone) */}
        {!isEpisode && (
          <div className="space-y-1">
            <Label htmlFor="order">Sort Order</Label>
            <Input id="order" type="number" min={0} {...register('order', { valueAsNumber: true })} />
            {errors.order && (
              <p className="text-sm text-destructive">{withErrorCode(String(errors.order.message), 'app/validation')}</p>
            )}
          </div>
        )}

        <div className="space-y-1">
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            rows={4}
            placeholder="Describe this film…"
            {...register('description')}
          />
          {errors.description && (
            <p className="text-sm text-destructive">{withErrorCode(String(errors.description.message), 'app/validation')}</p>
          )}
        </div>

        <div className="space-y-1">
          <Label htmlFor="youtubeId">YouTube Video ID</Label>
          <Input
            id="youtubeId"
            type="text"
            placeholder="dQw4w9WgXcQ"
            {...register('youtubeId')}
          />
          <p className="text-xs text-muted-foreground">
            The ID portion only (e.g. from https://youtube.com/watch?v=<strong>ID</strong>).
            {' '}Leave blank if using a self-hosted video below.
          </p>
          {errors.youtubeId && (
            <p className="text-sm text-destructive">{withErrorCode(String(errors.youtubeId.message), 'app/validation')}</p>
          )}
        </div>

        {/* Self-hosted video upload */}
        <div className="space-y-2 rounded-lg border p-4">
          <div className="space-y-1">
            <p className="text-sm font-medium">Self-Hosted Video</p>
            <p className="text-xs text-muted-foreground">
              Upload a video file to host directly. Takes priority over YouTube when set.
            </p>
          </div>
          {videoUrl && (
            <video
              src={videoUrl}
              className="w-full max-h-40 rounded object-contain bg-black"
              controls={false}
            />
          )}
          <div className="flex items-center gap-3">
            <input
              ref={videoFileInputRef}
              type="file"
              accept="video/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) void handleVideoUpload(file)
              }}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => videoFileInputRef.current?.click()}
              disabled={videoUploading || !watchedTitle}
            >
              {videoUploading ? `Uploading ${videoUploadProgress}%…` : videoUrl ? 'Replace' : 'Upload'}
            </Button>
            {videoUrl && !videoUploading && (
              <span className="text-xs text-green-600 font-medium">Uploaded</span>
            )}
            {!watchedTitle && (
              <p className="text-xs text-muted-foreground">Enter a title first.</p>
            )}
          </div>
        </div>

        {/* Thumbnail upload */}
        <div className="space-y-2 rounded-lg border p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <p className="text-sm font-medium">Custom Thumbnail</p>
              <p className="text-xs text-muted-foreground">
                Overrides the YouTube auto-generated thumbnail when displaying this film.
              </p>
              <p className="text-xs text-muted-foreground">
                Recommended: 1280 × 720 px (16:9)
              </p>
            </div>
            {thumbnailUrl && (
              <img
                src={thumbnailUrl}
                alt="Thumbnail preview"
                className="h-16 w-28 rounded object-cover border shrink-0"
              />
            )}
          </div>
          <div className="flex items-center gap-3">
            <input
              ref={thumbnailFileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) void handleThumbnailUpload(file)
              }}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => thumbnailFileInputRef.current?.click()}
              disabled={thumbnailUploading || !watchedTitle}
            >
              {thumbnailUploading ? 'Uploading…' : thumbnailUrl ? 'Replace' : 'Upload'}
            </Button>
            {thumbnailUrl && !thumbnailUploading && (
              <span className="text-xs text-green-600 font-medium">Uploaded</span>
            )}
            {!watchedTitle && (
              <p className="text-xs text-muted-foreground">Enter a title first.</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <input
            id="isPublished"
            type="checkbox"
            className="h-4 w-4 accent-primary"
            {...register('isPublished')}
          />
          <Label htmlFor="isPublished">Published</Label>
        </div>

        <MemberSearchPicker
          label="Choreographers"
          members={members}
          selected={choreographers}
          onChange={(updated) => setValue('choreographers', updated, { shouldValidate: true })}
        />

        <MemberSearchPicker
          label="Cast"
          members={members}
          selected={cast}
          onChange={(updated) => setValue('cast', updated, { shouldValidate: true })}
        />

        <MemberSearchPicker
          label="Crew"
          members={members}
          selected={crew}
          onChange={(updated) => setValue('crew', updated, { shouldValidate: true })}
        />

        {submitError && <p className="text-sm text-destructive">{submitError}</p>}

        <div className="flex gap-3 pt-2">
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Saving…' : isNew ? 'Create Film' : 'Save Changes'}
          </Button>
          <Button variant="outline" type="button" onClick={() => navigate(backTo)}>
            Cancel
          </Button>
        </div>
      </form>
    </AdminFormPage>
  )
}
